import classNames from 'classnames';
import type { RadioOption, RadioProps } from 'components/Radio';
import { Radio } from 'components/Radio';
import React, { useCallback, useState } from 'react';
import styles from './RadioGroup.module.scss';

// Props injected to child Radio components
interface RadioOptionInjectedProps<T extends string | number> {
  value: T;
  disabled?: boolean;
  name: string;
  description?: string;
  checked: boolean;
  className?: string;
  onChange?: React.FormEventHandler<HTMLInputElement>;
}

interface Props<T extends string | number>
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  name?: string;
  className?: string;
  onChange?: (value: T, e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedValue?: T;
  disabled?: boolean; // disable ALL radios
  options?: Array<RadioOption<T>>;

  // We could add a prop here to align radios horizontally if needed?
}

let counter = 0;
const generateName = () => `RadioGroup-${++counter}`;
export const RadioGroup = <T extends string | number>(props: Props<T>) => {
  const { options, children, className, selectedValue, disabled, name, onChange, ...rest } = props;

  if (!!options && !!children) {
    console.warn('Please use either children OR options');
  }

  const [groupName] = useState(name || generateName());
  const [selected, setSelected] = useState<T>();
  const realSelected = selectedValue !== undefined ? selectedValue : selected;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSelected(e.currentTarget.value as T);
      onChange?.(e.currentTarget.value as T, e);
    },
    [onChange]
  );

  const getInjectableRadioProps = (childProps: RadioProps<T>): RadioOptionInjectedProps<T> => {
    return {
      checked: realSelected === childProps.value,
      className: childProps.className,
      disabled: childProps.disabled || disabled,
      value: childProps.value,
      description: childProps.description,
      name: groupName,
      onChange: handleChange,
    };
  };

  return (
    <div className={classNames(styles.radioGroup, className)} {...rest}>
      {Array.isArray(options)
        ? options.map(option => {
            return (
              <Radio
                key={option.value}
                label={option.label || option.value.toString()}
                {...getInjectableRadioProps(option)}
              />
            );
          })
        : React.Children.map(children, child => {
            if (!React.isValidElement<RadioProps<T>>(child)) {
              return child;
            }
            return React.cloneElement(child, getInjectableRadioProps(child.props));
          })}
    </div>
  );
};
